import type {
  INextOpening,
  TAvailabilityStatus,
  TScheduleStatus,
} from './availability';
import { IDaySpan, IScheduleSummaryLine, SCHEDULE_WEEK } from './schedule-form';

/** vue-i18n's `$t`, bound. */
export type TTranslate = (
  key: string,
  params?: Record<string, unknown>
) => string;

export type TStatusTone = 'neutral' | 'amber';

export interface IStatusText {
  text: string;
  tone: TStatusTone;
  /** A hollow dot (hidden) instead of a solid one (out of hours). */
  hollow: boolean;
}

/** «a las 18:00», «mañana a las 12:00», «el lunes a las 12:00». */
export function openingText(t: TTranslate, next: INextOpening): string {
  if (next.dayOffset === 0)
    return t('menuPage.when.today', { time: next.time });
  if (next.dayOffset === 1)
    return t('menuPage.when.tomorrow', { time: next.time });
  return t('menuPage.when.weekday', {
    day: t(`menuPage.weekday.${next.weekday}`),
    time: next.time,
  });
}

/**
 * The status line of a product or category (plan §5.8): hidden states
 * first, then the schedule. null when it can simply be ordered.
 */
export function statusText(
  t: TTranslate,
  availability: TAvailabilityStatus | null,
  schedule: TScheduleStatus | null,
  branchName: string,
  kind: 'item' | 'category' = 'item'
): IStatusText | null {
  // A category is «Oculta», a product «Oculto».
  const group = kind === 'item' ? 'status' : 'categoryStatus';
  if (availability?.key === 'hidden')
    return {
      text: t(`menuPage.${group}.hidden`),
      tone: 'neutral',
      hollow: true,
    };
  if (availability?.key === 'hiddenHere')
    return {
      text: t(`menuPage.${group}.hiddenHere`, { branch: branchName }),
      tone: 'neutral',
      hollow: true,
    };
  if (schedule?.key === 'scheduleInactive')
    return {
      text: t('menuPage.status.scheduleInactive'),
      tone: 'neutral',
      hollow: false,
    };
  if (schedule?.key === 'outOfHours')
    return {
      text: schedule.next
        ? t('menuPage.status.outOfHoursBack', {
            when: openingText(t, schedule.next),
          })
        : t('menuPage.status.outOfHours'),
      tone: 'amber',
      hollow: false,
    };
  return null;
}

/** «lun a vie» → «Lun a vie» (a line or badge of its own). */
export function capitalize(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}

/** «lun a vie», «sáb, dom», «todos los días». */
export function daysText(t: TTranslate, spans: readonly IDaySpan[]): string {
  const days = spans.reduce(
    (count, span) =>
      count +
      SCHEDULE_WEEK.indexOf(span.to) -
      SCHEDULE_WEEK.indexOf(span.from) +
      1,
    0
  );
  if (spans.length === 1 && days === SCHEDULE_WEEK.length)
    return t('menuPage.schedule.everyDay');
  return spans
    .map((span) =>
      span.from === span.to
        ? t(`menuPage.weekdayShort.${span.from}`)
        : t('menuPage.schedule.dayRange', {
            from: t(`menuPage.weekdayShort.${span.from}`),
            to: t(`menuPage.weekdayShort.${span.to}`),
          })
    )
    .join(', ');
}

/**
 * A schedule in one short line for badges: «lun a vie · 12:00 a 16:00»,
 * or «{n} horarios» when it has several windows. null without a schedule.
 */
export function scheduleBadgeText(
  t: TTranslate,
  lines: readonly IScheduleSummaryLine[]
): string | null {
  if (!lines.length) return null;
  if (lines.length > 1)
    return t('menuPage.schedule.several', { n: lines.length });
  const [line] = lines;
  return t('menuPage.schedule.line', {
    days: daysText(t, line.spans),
    from: line.from,
    to: line.to,
  });
}
